import { getCookie } from "@/features/shared/cookies/cookie.utils";
import { useCookie } from "@/features/shared/cookies/use-cookie.hook";
import useGetCipUser from "@/hooks/use-get-cip-user.hook";
import {
  expireStaffSession,
  getStaffSessionToken,
  isStaffSessionExpiredError,
  onStaffSignOutFailed,
} from "@/services/staff-session.services";
import { getStaffUser, resolveStaffSession } from "@/services/staff.services";
import { CipUser } from "@EspaceParkQC/sdk-services-legacy";
import { createContext, PropsWithChildren, useContext, useEffect, useState } from "react";

/** "legacy" is today's central-server session, or nobody signed in yet. */
type StaffSessionMode = "checking" | "staff" | "legacy";

interface StaffSessionValue {
  mode: StaffSessionMode;
  user?: CipUser;
  error?: unknown;
}

const StaffSessionContext = createContext<StaffSessionValue>({ mode: "checking" });

/** Decides once how this page is signed in, then loads the staff user. Requests read the token themselves. */
export function StaffSessionProvider({ children }: PropsWithChildren): React.ReactElement {
  const [value, setValue] = useState<StaffSessionValue>({ mode: "checking" });

  useEffect(() => {
    let cancelled = false;

    // Shown as the critical error, whose Logout button clears what the sign-out could not.
    const stopListening = onStaffSignOutFailed((error) => {
      if (!cancelled) setValue((current) => ({ ...current, error }));
    });

    void resolveStaffSession(getCookie<string>("cnp_cip_token")).then(async (isStaff) => {
      if (cancelled) return;
      if (!isStaff) {
        setValue({ mode: "legacy" });
        return;
      }

      setValue({ mode: "staff" });

      try {
        const token = await getStaffSessionToken();
        if (!token) throw new Error("No staff member is signed in.");

        const user = await getStaffUser(token);
        if (!cancelled) setValue((current) => ({ ...current, user }));
      } catch (error) {
        // An answer that comes after this provider is gone must not end whichever session runs now.
        if (cancelled) return;

        // The API's clock decides: a sign-in it finds too old ends here, whatever this browser's clock says.
        if (isStaffSessionExpiredError(error)) void expireStaffSession();
        else setValue((current) => ({ ...current, error }));
      }
    });

    return () => {
      cancelled = true;
      stopListening();
    };
  }, []);

  return <StaffSessionContext.Provider value={value}>{children}</StaffSessionContext.Provider>;
}

export function useStaffSession(): StaffSessionValue {
  return useContext(StaffSessionContext);
}

/**
 * The signed-in agent and their clicknpark API credential, whichever session this page runs on: a
 * function that reads the staff token before each request, or the central-server session's token.
 */
export function useCallCenterSession(): {
  user?: CipUser;
  token?: string;
  getToken?: () => Promise<string | undefined>;
} {
  const staff = useStaffSession();
  const { data: legacyUser } = useGetCipUser();
  const { value: legacyToken } = useCookie<string>("cnp_services_token");

  return staff.mode === "staff"
    ? { user: staff.user, getToken: getStaffSessionToken }
    : { user: legacyUser, token: legacyToken };
}
